import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { flagsAPI } from '../../services/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeft, Loader2, Send, User, Building, Flag, CheckCircle } from 'lucide-react';

const FlagThread = () => {
  const { recordId, flagId } = useParams();
  const { user } = useAuth();
  const [flag, setFlag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchFlag();
  }, [flagId]);

  const fetchFlag = async () => {
    try {
      const res = await flagsAPI.get(flagId);
      setFlag(res.data);
    } catch (error) {
      console.error('Error fetching flag:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    
    setSending(true);
    try {
      await flagsAPI.addMessage(flagId, message);
      setMessage('');
      fetchFlag();
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    try {
      await flagsAPI.resolve(flagId);
      fetchFlag();
      toast.success('Flag resolved');
    } catch (error) {
      toast.error('Failed to resolve flag');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  if (!flag) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-[#0A0F1E]">Flag not found</h2>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl space-y-6" data-testid="employee-flag-thread">
        {/* Back Link */}
        <Link 
          to={`/employee/records/${recordId}`} 
          className="inline-flex items-center gap-2 text-[#5B6472] hover:text-[#0A0F1E] transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Record
        </Link>

        {/* Flag Info */}
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Flag className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-[#0A0F1E] capitalize">
                  {flag.field_name?.replace(/_/g, ' ')}
                </h1>
                <p className="text-[#5B6472] text-sm">Flagged field</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`px-3 py-1.5 rounded-full text-sm ${
                flag.status === 'resolved' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>
                {flag.status}
              </span>
              {flag.status === 'open' && (
                <Button
                  onClick={handleResolve}
                  variant="outline"
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10 rounded-xl"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Mark Resolved
                </Button>
              )}
            </div>
          </div>
          
          <div className="p-4 rounded-lg bg-[#F5F8FF]/50 border border-[#E8EDF5]">
            <p className="text-[#5B6472] text-sm mb-2">Your concern:</p>
            <p className="text-[#0A0F1E]">{flag.employee_message}</p>
          </div>
        </Card>

        {/* Messages */}
        <div className="space-y-4">
          {flag.messages?.map((msg) => (
            <div key={msg.id} className={`flex gap-3 ${msg.sender_type === 'employee' ? 'flex-row-reverse' : ''}`}>
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                msg.sender_type === 'employee' ? 'bg-[#1B5AFF]/20' : 'bg-[#F5F8FF] border border-[#E8EDF5]'
              }`}>
                {msg.sender_type === 'employee' ? (
                  <User className="w-4 h-4 text-[#1B5AFF]" />
                ) : (
                  <Building className="w-4 h-4 text-[#5B6472]" />
                )}
              </div>
              <Card className={`flex-1 rounded-xl p-4 ${
                msg.sender_type === 'employee' 
                  ? 'bg-[#1B5AFF]/10 border border-[#1B5AFF]/30' 
                  : 'bg-[#FFFFFF] border border-[#E8EDF5]'
              }`}>
                <p className="text-[#5B6472] text-xs mb-2">{msg.sender_name}</p>
                <p className="text-[#0A0F1E]">{msg.message}</p>
                <p className="text-[#5B6472] text-xs mt-2">
                  {new Date(msg.created_at).toLocaleString()}
                </p>
              </Card>
            </div>
          ))}
        </div>

        {/* Reply Input */}
        {flag.status === 'open' && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-4">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[100px] mb-4"
            />
            <div className="flex justify-end">
              <Button
                onClick={handleSend}
                disabled={sending || !message.trim()}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              >
                {sending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Send className="mr-2 h-4 w-4" />
                )}
                Send Message
              </Button>
            </div>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FlagThread;
